import React, { useEffect, useState, useRef, useMemo } from "react"
import * as url from "../../helpers/url_helper"
import { calculateStartIndex } from "helpers/pagination_helper"

import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Label,
    Row,
} from "reactstrap"

import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import {
    manageUser,
    getUserDetail,
    clearUserDetail,
    getAuthorizationGroups,
    clearManageError,
} from "store/users/actions"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import ConfirmButton from "components/Common/ConfirmButton"
import TextInput from "components/Common/TextInput"
import SelectInput from "components/Common/SelectInput"
import CButton from "components/Common/CButton"
import AccessControlWrapper from "components/Common/PermissionButton"

export default function UserForm({
    page,
    match,
    history,
    onUserCreated,
    onTabCompletion,
    navigateToNextTab,
    activeTab,
    onFormRef,
    onReadOnlyChange,
    onCancel,
    onSuccess,
    isModalMode,
    currentPage,
    searchKeyword,
}) {
    const dispatch = useDispatch()

    const [selectedUserGroups, setSelectedUserGroups] = useState([])
    const [readOnly, setReadOnly] = useState(page === "detail")
    const [createdUserID, setCreatedUserID] = useState(null)

    const previousCreationSuccess = useRef(null)
    const validationRef = useRef(null)

    const userID = match?.params?.id
    const isEditMode = page === "detail"

    const { user, loading, error, manageError, creationSuccess, authorizationGroups } = useSelector(state => ({
        user: state.users.user,
        loading: state.users.loading,
        error: state.users.error,
        manageError: state.users.manageError,
        creationSuccess: state.users.creationSuccess,
        authorizationGroups: state.users.authorizationGroups || [],
    }))

    // Reset readonly state when page changes to add mode
    useEffect(() => {
        if (page === "add") {
            setReadOnly(false)
        }
    }, [page])

    // User Groups options - dynamic from API
    const userGroupOptions = useMemo(() => {
        return authorizationGroups.map(group => ({
            label: group.groupName,
            value: group.groupCode,
            groupId: group.groupId || group.groupID // Support both lowercase and uppercase
        }))
    }, [authorizationGroups])

    // Load user detail if in edit mode, clear if add mode
    // Skip API call if in modal mode - data is already populated from list
    useEffect(() => {
        if (isEditMode && userID && !isModalMode) {
            dispatch(
                getUserDetail({
                    headers: {
                        "input-parameters": {
                            userID: userID,
                        },
                    },
                }, history)
            )
        } else if (page === "add") {
            // Clear any existing user data when adding new user
            dispatch(clearUserDetail())
        }

        return () => {
            // Only clear on unmount if not in modal mode (modal handles its own cleanup)
            if (!isModalMode) {
                dispatch(clearUserDetail())
            }
        }
    }, [dispatch, isEditMode, userID, page, history, isModalMode])

    // Load authorization groups on component mount
    useEffect(() => {
        dispatch(getAuthorizationGroups({
            headers: {
                "input-parameters": {},
            },
        }, history))
    }, [dispatch, history])

    // Clear user data and manage errors when component mounts/unmounts
    useEffect(() => {
        // Clear any lingering manage errors when component mounts
        dispatch(clearManageError())

        return () => {
            dispatch(clearUserDetail())
            dispatch(clearManageError())
        }
    }, [dispatch])

    // Update form when user data is loaded
    useEffect(() => {
        if (isEditMode && user?.userID) {
            // Handle userGroups - support both object array and string array structures
            let userGroupCodes = []
            if (user.userGroups && Array.isArray(user.userGroups)) {
                if (user.userGroups.length > 0 && typeof user.userGroups[0] === 'object') {
                    // New structure: array of objects with { groupId, groupCode, groupName, comments }
                    userGroupCodes = user.userGroups.map(group => group.groupCode)
                } else {
                    // Legacy structure: array of strings (groupCodes)
                    userGroupCodes = user.userGroups
                }
            }

            validation.setValues({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                mobile: user.mobile || "",
                email: user.email || "",
                password: "",
                confirmPassword: "",
                userGroups: userGroupCodes,
            })

            setSelectedUserGroups(userGroupCodes)
        }
    }, [user, isEditMode, authorizationGroups])

    // Handle user creation/update success
    useEffect(() => {
        if (
            creationSuccess &&
            creationSuccess !== previousCreationSuccess.current
        ) {
            if (!isEditMode) {
                setReadOnly(true)
                setCreatedUserID(creationSuccess.userID || Date.now().toString())
                if (onReadOnlyChange) onReadOnlyChange(true)
                if (onUserCreated) onUserCreated(creationSuccess.userID)
                if (onTabCompletion) onTabCompletion("1")
                // For modal mode, call onSuccess callback
                if (isModalMode && onSuccess) {
                    onSuccess()
                }
            } else {
                // For edit mode, also set readonly to true after successful update
                setReadOnly(true)
                if (onReadOnlyChange) onReadOnlyChange(true)
                // For modal mode, call onSuccess callback
                if (isModalMode && onSuccess) {
                    onSuccess()
                }
            }

            previousCreationSuccess.current = creationSuccess
        }
    }, [
        creationSuccess,
        isEditMode,
        onUserCreated,
        onReadOnlyChange,
        onTabCompletion,
        isModalMode,
        onSuccess,
    ])

    // Expose form methods to parent component
    useEffect(() => {
        if (onFormRef && page === "add") {
            onFormRef({
                handleSubmit: async () => {
                    const isValid = await validateBeforeConfirm(validationRef.current)
                    if (isValid) {
                        handleConfirmedSubmit()
                        return true
                    }
                    return false
                },
                resetForm: () => {
                    validationRef.current?.resetForm()
                },
                isReadOnly: readOnly
            })
        }
    }, [onFormRef, page, readOnly])

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstName: "",
            lastName: "",
            mobile: "",
            email: "",
            password: "",
            confirmPassword: "",
            userGroups: [],
        },
        validationSchema: Yup.object(
            isEditMode
                ? {
                    // In edit mode, only userGroups are editable and required
                    userGroups: Yup.array().min(1, "Please select at least one user group").required("Please select user groups"),
                }
                : {
                    // In add mode, validate all fields
                    firstName: Yup.string().max(255, "First name must be 255 characters or less").required("Please enter first name"),
                    lastName: Yup.string().max(255, "Last name must be 255 characters or less").required("Please enter last name"),
                    mobile: Yup.string().max(15, "Mobile must be 15 characters or less").required("Please enter mobile number"),
                    email: Yup.string().email("Please enter valid email").max(255, "Email must be 255 characters or less").required("Please enter email"),
                    password: Yup.string().min(5, "Password must be at least 5 characters").max(9, "Password must be 9 characters or less").required("Please enter password"),
                    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match').min(5, "Confirm password must be at least 5 characters").max(9, "Confirm password must be 9 characters or less").required("Please confirm password"),
                    userGroups: Yup.array().min(1, "Please select at least one user group").required("Please select user groups"),
                }
        ),
        onSubmit: async () => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Update validation ref whenever validation changes
    useEffect(() => {
        validationRef.current = validation
    }, [validation])

    // Function to handle the actual form submission after confirmation
    const handleConfirmedSubmit = async () => {
        try {
            const values = validationRef.current.values
            const actionType = (isEditMode || createdUserID) ? "update" : "create"

            // Convert selectedUserGroups (groupCodes) to groupIDs by matching with API response
            const userGroupIDs = selectedUserGroups.map(groupCode => {
                const group = authorizationGroups.find(g => g.groupCode === groupCode)
                // Support both lowercase and uppercase field names
                const id = group ? (group.groupId || group.groupID) : null
                return id
            }).filter(Boolean).map(id => id.toString())

            const payload = {
                actionType,
                userGroupIDs, // Send userGroupIDs as array of strings
            }

            // Add userID for update operations
            if (isEditMode && userID) {
                payload.userID = userID.toString()
            } else if (createdUserID) {
                payload.userID = createdUserID.toString()
            }

            // In create mode (first time), include all user creation fields (confirmPassword is excluded)
            if (!isEditMode && !createdUserID) {
                payload.firstName = values.firstName
                payload.lastName = values.lastName
                payload.mobile = values.mobile
                payload.email = values.email
                payload.password = values.password // confirmPassword is not included
            }

            // Build options object
            const options = {
                headers: {
                    "input-parameters": payload,
                },
            }

            // In modal mode, pass refreshPagination so saga can refresh with correct page
            if (isModalMode && currentPage !== undefined) {
                options.refreshPagination = {
                    startIndex: calculateStartIndex(currentPage),
                    keyword: searchKeyword || "",
                }
            }

            dispatch(manageUser(payload, options, history))
        } catch (error) {
            console.error("User submission failed:", error)
        }
    }

    const handleUserGroupChange = (selectedOptions) => {
        let selectedGroups = selectedOptions || []
        let values = selectedGroups.map(option => option.value)

        // Check if ADMIN is being selected (look for admin in groupCode)
        const adminSelected = values.some(value => value && value.toLowerCase().includes('admin'))
        const prevAdminSelected = selectedUserGroups.some(value => value && value.toLowerCase().includes('admin'))

        // If ADMIN is selected, only allow ADMIN (cannot be combined with other groups)
        if (adminSelected) {
            const adminGroup = selectedGroups.find(group => group.value && group.value.toLowerCase().includes('admin'))
            if (adminGroup) {
                values = [adminGroup.value]
                selectedGroups = [adminGroup]
            }
        }
        // If ADMIN was previously selected and is being deselected, allow other selections
        else if (prevAdminSelected && !adminSelected) {
            // Remove ADMIN and keep other selections
            values = values.filter(value => !value || !value.toLowerCase().includes('admin'))
            selectedGroups = selectedGroups.filter(group => !group.value || !group.value.toLowerCase().includes('admin'))
        }

        setSelectedUserGroups(values)
        validation.setFieldValue("userGroups", values)
    }

    return (
        <React.Fragment>
            {((page === "detail" && readOnly) || (page === "add" && readOnly)) && (
                <div className="mb-3 d-flex justify-content-end">
                    <AccessControlWrapper
                        contextUri={url.MANAGE_USER}
                        actionType="post"
                    >
                        <CButton.Primary
                            text="Edit User"
                            onClick={() => {
                                setReadOnly(false)
                                if (onReadOnlyChange) {
                                    onReadOnlyChange(false)
                                }
                            }}
                        />
                    </AccessControlWrapper>
                </div>
            )}
            {/* Manage User Error Alert (for create/update operations) */}
            {manageError?.message && (
                <Row>
                    <Col lg="12">
                        <Alert color="danger">
                            {manageError.message}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* General Error Alert (for data loading) */}
            {error?.message && (
                <Row>
                    <Col lg="12">
                        <Alert color="danger">{error.message}</Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col lg="12">
                    <Card>
                        <CardBody>
                            <Form
                                className="needs-validation"
                                onSubmit={e => {
                                    e.preventDefault()
                                    validation.handleSubmit()
                                    return false
                                }}
                            >
                                {/* Basic user information - always show */}

                                <Row>
                                    <Col md="6">
                                        <TextInput
                                            type="text"
                                            name="email"
                                            label="Email"
                                            placeholder="Enter email address"
                                            helperText="Max 255 chars allowed"
                                            maxLength={255}
                                            validation={validation}
                                            readOnly={isEditMode || readOnly || createdUserID}
                                            required={!isEditMode}
                                        />
                                    </Col>
                                    <Col md="6">
                                        <TextInput
                                            type="text"
                                            name="mobile"
                                            label="Mobile"
                                            placeholder="Enter mobile number"
                                            helperText="Max 15 chars allowed"
                                            maxLength={15}
                                            validation={validation}
                                            readOnly={isEditMode || readOnly || createdUserID}
                                            required={!isEditMode}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="6">
                                        <TextInput
                                            type="text"
                                            name="firstName"
                                            label="First Name"
                                            placeholder="Enter first name"
                                            helperText="Max 255 chars allowed"
                                            maxLength={255}
                                            validation={validation}
                                            readOnly={isEditMode || readOnly || createdUserID}
                                            required={!isEditMode}
                                        />
                                    </Col>
                                    <Col md="6">
                                        <TextInput
                                            type="text"
                                            name="lastName"
                                            label="Last Name"
                                            placeholder="Enter last name"
                                            helperText="Max 255 chars allowed"
                                            maxLength={255}
                                            validation={validation}
                                            readOnly={isEditMode || readOnly || createdUserID}
                                            required={!isEditMode}
                                        />
                                    </Col>
                                </Row>

                                {/* Password fields - only show in add mode, not readonly, and user not created yet */}
                                {!isEditMode && !readOnly && !createdUserID && (
                                    <Row>
                                        <Col md="6">
                                            <TextInput
                                                type="password"
                                                name="password"
                                                label="Password"
                                                placeholder="Enter password"
                                                helperText="Min 5 and Max 9 chars allowed"
                                                maxLength={9}
                                                validation={validation}
                                                readOnly={readOnly}
                                                required={true}
                                            />
                                        </Col>
                                        <Col md="6">
                                            <TextInput
                                                type="password"
                                                name="confirmPassword"
                                                label="Confirm Password"
                                                placeholder="Confirm password"
                                                maxLength={9}
                                                validation={validation}
                                                readOnly={readOnly}
                                                required={true}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                <Row>
                                    <Col md="6">
                                        <SelectInput
                                            name="userGroups"
                                            label="User Groups"
                                            placeholder="Select user groups"
                                            options={userGroupOptions}
                                            isMulti={true}
                                            {...(!readOnly && { value: userGroupOptions.filter(option => selectedUserGroups.includes(option.value)) })}
                                            onChange={handleUserGroupChange}
                                            validation={validation}
                                            readOnly={readOnly}
                                            required={true}
                                        />
                                    </Col>
                                    {
                                        isEditMode && readOnly && (
                                            <Col md="6">
                                                <FormGroup className="mb-3">
                                                    <Label>Status</Label>
                                                    <div className="form-control input-as-text">
                                                        <span>
                                                            {user?.userState}
                                                        </span>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                        )
                                    }
                                </Row>

                                {/* Submit Button - Only show for detail/edit mode or add mode after creation */}
                                {!readOnly && (page === "detail" || (page === "add" && createdUserID)) && (
                                    <Row>
                                        <Col md="12" className="text-end">
                                            <CButton.Secondary
                                                text="Cancel"
                                                className="me-2"
                                                onClick={() => {
                                                    setReadOnly(true)
                                                    if (onReadOnlyChange) {
                                                        onReadOnlyChange(true)
                                                    }

                                                    // Reset form to original values
                                                    if (page === "detail" && user?.userID) {
                                                        // Handle userGroups - support both object array and string array structures
                                                        let userGroupCodes = []
                                                        if (user.userGroups && Array.isArray(user.userGroups)) {
                                                            if (user.userGroups.length > 0 && typeof user.userGroups[0] === 'object') {
                                                                // New structure: array of objects with { groupId, groupCode, groupName, comments }
                                                                userGroupCodes = user.userGroups.map(group => group.groupCode)
                                                            } else {
                                                                // Legacy structure: array of strings (groupCodes)
                                                                userGroupCodes = user.userGroups
                                                            }
                                                        }

                                                        validation.setValues({
                                                            firstName: user.firstName || "",
                                                            lastName: user.lastName || "",
                                                            mobile: user.mobile || "",
                                                            email: user.email || "",
                                                            password: "",
                                                            confirmPassword: "",
                                                            userGroups: userGroupCodes,
                                                        })

                                                        setSelectedUserGroups(userGroupCodes)
                                                    }
                                                }}
                                                disabled={loading}
                                            />
                                            <ConfirmButton
                                                validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                                onConfirm={handleConfirmedSubmit}
                                                message="Are you sure you want to update this User? This action will modify the existing User information."
                                                color="primary"
                                                disabled={loading}
                                                contextUri={url.MANAGE_USER}
                                                actionType="post"
                                            >
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    "Update User"
                                                )}
                                            </ConfirmButton>
                                        </Col>
                                    </Row>
                                )}

                                {/* Create Button - Only show for add mode when not readonly and user not created yet */}
                                {!readOnly && !createdUserID && page === "add" && (
                                    <Row>
                                        <Col md="12" className="text-end">
                                            <CButton.Secondary
                                                text="Cancel"
                                                className="me-2"
                                                onClick={() => {
                                                    if (isModalMode && onCancel) {
                                                        onCancel()
                                                    } else {
                                                        history.push("/users")
                                                    }
                                                }}
                                                disabled={loading}
                                            />
                                            <ConfirmButton
                                                validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                                onConfirm={handleConfirmedSubmit}
                                                message="Are you sure you want to create this User?"
                                                color="primary"
                                                disabled={loading}
                                                contextUri={url.MANAGE_USER}
                                                actionType="post"
                                            >
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Creating...
                                                    </>
                                                ) : (
                                                    "Create User"
                                                )}
                                            </ConfirmButton>
                                        </Col>
                                    </Row>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}
